import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, MapPin, Tag, Calendar, Euro, Send, AlertTriangle,
  CheckCircle2, MessageSquare, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  freelanceGigsApi,
  FreelanceGig,
  FreelanceGigResponseRow,
} from '../services/freelanceGigsApi';
import './FreelanceGigs.scss';

/**
 * Employee-facing public gig page.
 *
 *   - Anonymous employer view (we never expose the employer's identity here).
 *   - Shows full gig details + an "Įgalu — siųsti pasiūlymą" button if the
 *     employee hasn't responded yet.
 *   - If the employee already responded, shows a thread shortcut + a
 *     "Atsisakyti" button to withdraw.
 */
const FreelanceGigPublic: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [gig, setGig] = useState<FreelanceGig | null>(null);
  const [myResponse, setMyResponse] = useState<FreelanceGigResponseRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [introMessage, setIntroMessage] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const gigId = id ? parseInt(id, 10) : NaN;

  const load = useCallback(async () => {
    if (Number.isNaN(gigId)) return;
    setLoading(true);
    try {
      const data = await freelanceGigsApi.publicGig(gigId);
      setGig(data.gig);
      setMyResponse(data.my_response);
    } catch (err: any) {
      setActionError(err?.message || 'Nepavyko įkrauti Gig');
    } finally {
      setLoading(false);
    }
  }, [gigId]);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employee') load();
  }, [isAuthenticated, user, load]);

  if (isLoading || loading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.user_type !== 'employee') return <Navigate to="/" replace />;
  if (!gig) {
    return (
      <div className="freelance-create-page">
        <button className="freelance-back-link" onClick={() => navigate('/freelance/browse')}>
          <ChevronLeft size={16} /> Atgal
        </button>
        <div className="freelance-alert freelance-alert--error">
          {actionError || 'Gig nerastas'}
        </div>
      </div>
    );
  }

  const onSubmitResponse = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      const { response } = await freelanceGigsApi.respond(gig.id, introMessage.trim() || undefined);
      setMyResponse(response);
      setShowRespondModal(false);
      setIntroMessage('');
      navigate(`/freelance/messages/${response.id}`);
    } catch (err: any) {
      setActionError(err?.message || 'Nepavyko išsiųsti pasiūlymo');
    } finally {
      setActionBusy(false);
    }
  };

  const onWithdraw = async () => {
    if (!myResponse) return;
    if (!window.confirm('Ar tikrai atsisakyti?')) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const { response } = await freelanceGigsApi.withdraw(myResponse.id);
      setMyResponse(response);
    } catch (err: any) {
      setActionError(err?.message || 'Nepavyko atsisakyti');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="freelance-detail-page">
      <button className="freelance-back-link" onClick={() => navigate('/freelance/browse')}>
        <ChevronLeft size={16} /> Visi Gigai
      </button>

      <div className="freelance-detail-header">
        <h1 className="freelance-detail-title">{gig.title}</h1>

        <div className="freelance-detail-actions">
          {!myResponse && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={actionBusy}
              onClick={() => setShowRespondModal(true)}
            >
              <Send size={14} /> Susisiekti
            </button>
          )}
          {myResponse?.status === 'interested' && (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(`/freelance/messages/${myResponse.id}`)}
              >
                <MessageSquare size={14} /> Žinutės
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={actionBusy}
                onClick={onWithdraw}
              >
                <X size={14} /> Atsisakyti
              </button>
            </>
          )}
          {myResponse?.status === 'withdrawn' && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={actionBusy}
              onClick={() => setShowRespondModal(true)}
            >
              <Send size={14} /> Vėl susisiekti
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <div className="freelance-alert freelance-alert--error">
          <AlertTriangle size={16} /> {actionError}
        </div>
      )}

      {myResponse?.status === 'interested' && (
        <div className="freelance-alert freelance-alert--success">
          <CheckCircle2 size={16} /> Pasiūlymas išsiųstas. Laukite darbdavio atsakymo arba parašykite žinutę.
        </div>
      )}

      <section className="freelance-card">
        <h2>Aprašymas</h2>
        <p className="freelance-detail-desc">{gig.description}</p>

        <div className="freelance-detail-meta">
          {gig.work_fields.length > 0 && (
            <div className="freelance-meta-row"><Tag size={14} />
              <strong>Sritys:</strong> {gig.work_fields.join(', ')}
            </div>
          )}
          <div className="freelance-meta-row"><MapPin size={14} />
            <strong>Vieta:</strong> {gig.nationwide ? 'Visoje Lietuvoje' : (gig.city || 'Nenurodyta')}
          </div>
          {gig.deadline_text && (
            <div className="freelance-meta-row"><Calendar size={14} />
              <strong>Terminas:</strong> {gig.deadline_text}
            </div>
          )}
          {gig.budget_text && (
            <div className="freelance-meta-row"><Euro size={14} />
              <strong>Biudžetas:</strong> {gig.budget_text}
            </div>
          )}
        </div>
      </section>

      {showRespondModal && (
        <div className="freelance-modal-overlay" onClick={() => setShowRespondModal(false)}>
          <div className="freelance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="freelance-modal-header">
              <h3>Trumpa žinutė darbdaviui</h3>
              <button type="button" className="freelance-icon-btn" onClick={() => setShowRespondModal(false)}>
                <X size={18} />
              </button>
            </div>
            <textarea
              rows={6}
              maxLength={2000}
              value={introMessage}
              onChange={(e) => setIntroMessage(e.target.value)}
              placeholder="Pristatykite save trumpai. Galite palikti tuščią ir parašyti vėliau."
              className="freelance-modal-textarea"
            />
            <div className="freelance-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRespondModal(false)}>
                Atšaukti
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={actionBusy}
                onClick={onSubmitResponse}
              >
                <Send size={14} /> Siųsti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreelanceGigPublic;
